import { useState, useEffect, FormEvent } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApiToken } from "@/components/providers/token-provider";
import {
  authApi,
  type OrgConfigResponse,
  type SSOIdPDescriptor,
} from "@/api/services";
import { getOrgName } from "@/config/organization";
import { SSO_REDIRECT_KEY, deriveOrgAuthUi, sanitizeRedirectPath } from "@/lib/auth";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import DevOrgSwitcher from "@/components/dev/DevOrgSwitcher";

const urbintLogoWhite = "/logos/urbint/Urbint Logo - White Target Only.png";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, isTokenLoaded } = useApiToken();
  const [mounted, setMounted] = useState(false);

  // Get the intended destination from location state, query param, or default to home
  // Priority: location state > query param > home
  // Sanitized to a same-origin path: ``?redirect=`` is attacker-controllable,
  // so an absolute/protocol-relative target must not be replayed into
  // navigate() (open redirect → post-login phishing).
  const stateFrom = (location.state as { from?: { pathname: string } })?.from?.pathname;
  const queryRedirect = searchParams.get("redirect");
  const from = sanitizeRedirectPath(stateFrom || queryRedirect);

  // View state: 'login' | 'forgot-password'
  const [view, setView] = useState<"login" | "forgot-password">("login");

  // Org config (which login methods this org supports). Loaded on mount.
  // Null = still loading; non-null = decided what to render.
  const [orgConfig, setOrgConfig] = useState<OrgConfigResponse | null>(null);

  // Login State
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [ssoRedirecting, setSsoRedirecting] = useState(false);

  // Forgot Password State
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    setMounted(true);
    const flash = sessionStorage.getItem("auth:flash");
    if (flash) {
      sessionStorage.removeItem("auth:flash");
      setTimeout(() => toast.error(flash), 0);
    }
  }, []);

  // Fetch the org's auth config so we know which login methods to show.
  // org_slug is read from the subdomain (handled by getOrgName()).
  useEffect(() => {
    let cancelled = false;
    authApi
      .getOrgConfig(getOrgName())
      .then((cfg) => {
        if (!cancelled) setOrgConfig(cfg);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error("Failed to fetch org config", e);
        // Fall back to the password form to keep the legacy behaviour.
        setOrgConfig({
          org_slug: getOrgName(),
          org_name: getOrgName(),
          auth_mode: "password_only",
          sso_enabled: false,
          idps: [],
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSSOClick = async (idpAlias: string) => {
    if (!orgConfig) return;
    setSsoRedirecting(true);
    if (from && from !== "/") {
      sessionStorage.setItem(SSO_REDIRECT_KEY, from);
    }
    try {
      // XHR to the backend: sets the si_sso_state HttpOnly cookie via
      // Set-Cookie and returns the Keycloak authorization URL.
      const result = await authApi.initSso(orgConfig.org_slug, idpAlias);
      // Top-level navigation to Keycloak — browser follows the redirect
      // chain to the IdP (Azure AD, Okta, etc.).
      globalThis.location.href = result.sso_url;
    } catch (err) {
      console.error("SSO init failed:", err);
      setSsoRedirecting(false);
      toast.error("Failed to initiate SSO login. Please try again.");
    }
  };

  // Redirect authenticated users away from login page
  useEffect(() => {
    if (isTokenLoaded && isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isTokenLoaded, isAuthenticated, navigate, from]);

  const handleEmailBlur = async () => {
    if (email && email.includes("@") && view === "login") {
      try {
        const response = await authApi.checkMfaStatus(email);
        setShowOtp(response.enabled);
      } catch (e) {
        // Silently fail
        console.error("Failed to check MFA status", e);
      }
    }
  };

  const handleLoginSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    if (showOtp && !otp) {
      setError("Please enter the authenticator code");
      return;
    }

    setIsLoading(true);

    try {
      const success = await login(email, password, otp);
      if (success) {
        // Navigate to the intended destination or home
        navigate(from, { replace: true });
      } else {
        setError("Invalid credentials");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (!resetEmail) {
      setError("Please enter your email address");
      return;
    }

    setResetLoading(true);

    try {
      await authApi.forgotPassword(resetEmail);
      setResetSuccess(true);
      toast.success("Password reset email sent");
    } catch (err) {
      setError("Failed to process request. Please try again.");
    } finally {
      setResetLoading(false);
    }
  };

  // Derive UI flags once so the SSO / password / divider sections stay
  // mutually consistent if the backend payload is inconsistent.
  const authUi = deriveOrgAuthUi(orgConfig);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-background to-muted/20">
      {/* Header with branding */}
      <header
        className="w-full border-b"
        style={{ backgroundColor: "var(--midnight-blue)" }}
      >
        <div className="flex h-16 w-full items-center justify-between gap-3 px-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <div className="flex shrink-0 items-center">
              {mounted ? (
                <img
                  src={urbintLogoWhite}
                  alt="Urbint Logo"
                  className="h-10 w-10 shrink-0 object-contain"
                />
              ) : (
                <div className="h-10 w-10 shrink-0 animate-pulse rounded-lg bg-white/20" />
              )}
            </div>
            <div className="flex h-8 shrink-0 items-center">
              <div className="h-full w-px bg-border" />
            </div>
            <div className="flex h-8 min-w-0 items-center sm:hidden">
              <p className="whitespace-nowrap text-sm font-medium text-white">
                <abbr
                  title="Emergency Preparedness & Response"
                  className="cursor-help no-underline"
                >
                  EP&amp;R
                </abbr>
              </p>
            </div>
            <div className="hidden h-8 min-w-0 items-center sm:flex">
              <p className="truncate text-sm font-medium text-white">
                EMERGENCY PREPAREDNESS & RESPONSE
              </p>
            </div>
          </div>
          <div className="shrink-0">
            <DevOrgSwitcher />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">
              {view === "login" ? "Sign In" : "Reset Password"}
            </CardTitle>
            <CardDescription>
              {view === "login"
                ? "Enter your credentials to access the application"
                : "Enter your email to receive a password reset link"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {view === "login" ? (
              <div className="space-y-4">
                {/* Loading skeleton until /auth/org-config returns. Keeps
                    layout stable so the right UI for the org appears
                    without the password form flashing first. */}
                {!orgConfig && (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                )}

                {/* SSO buttons. Shown for ``sso_only`` and ``both``.
                    Decision: per the MoM, ``sso_only`` does NOT
                    auto-redirect — we show buttons the user clicks. */}
                {authUi.showSso && orgConfig && (
                  <div className="space-y-2">
                    {orgConfig.idps.map((idp: SSOIdPDescriptor) => (
                      <Button
                        key={idp.alias}
                        type="button"
                        variant="outline"
                        className="w-full"
                        disabled={ssoRedirecting}
                        onClick={() => handleSSOClick(idp.alias)}
                      >
                        {ssoRedirecting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Redirecting…
                          </>
                        ) : (
                          `Sign in with ${idp.display_name}`
                        )}
                      </Button>
                    ))}
                  </div>
                )}

                {/* "Or" divider — only shown when both methods are available. */}
                {authUi.showDivider && (
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex-1 border-t" />
                    <span>or</span>
                    <span className="flex-1 border-t" />
                  </div>
                )}

                {/* Password form. Shown for ``password_only`` and ``both``. */}
                {authUi.showPassword && (
              <form onSubmit={handleLoginSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">
                    Email
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={handleEmailBlur}
                    disabled={isLoading}
                    autoComplete="email"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label htmlFor="password" className="text-sm font-medium">
                      Password
                    </label>
                    <Button
                      variant="link"
                      className="px-0 font-normal h-auto text-xs"
                      type="button"
                      onClick={() => {
                        setView("forgot-password");
                        setError("");
                      }}
                    >
                      Forgot password?
                    </Button>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    autoComplete="current-password"
                  />
                </div>

                {showOtp && (
                  <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                    <label htmlFor="otp" className="text-sm font-medium">
                      Authenticator Code
                    </label>
                    <Input
                      id="otp"
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      disabled={isLoading}
                      autoComplete="one-time-code"
                      maxLength={6}
                    />
                  </div>
                )}

                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                    {error}
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </form>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {resetSuccess ? (
                  <div className="text-center space-y-4 animate-in fade-in zoom-in">
                    <div className="mx-auto w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-medium text-lg">Check your email</h3>
                      <p className="text-sm text-muted-foreground">
                        We have sent a password reset link to <strong>{resetEmail}</strong>.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setView("login");
                        setResetSuccess(false);
                        setResetEmail("");
                      }}
                    >
                      Back to Sign In
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <label htmlFor="reset-email" className="text-sm font-medium">
                        Email Address
                      </label>
                      <Input
                        id="reset-email"
                        type="email"
                        placeholder="Enter your email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        disabled={resetLoading}
                        autoComplete="email"
                      />
                    </div>

                    {error && (
                      <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                        {error}
                      </div>
                    )}

                    <Button type="submit" className="w-full" disabled={resetLoading}>
                      {resetLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending Link...
                        </>
                      ) : (
                        "Send Reset Link"
                      )}
                    </Button>

                    <Button
                      variant="ghost"
                      type="button"
                      className="w-full"
                      onClick={() => {
                        setView("login");
                        setError("");
                      }}
                      disabled={resetLoading}
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back to Sign In
                    </Button>
                  </form>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
